// The instrument panel (BUILD-BRIEF-R2 §13 Agents): the health band, the five-cell rail counted
// from the ledger, the Principals panel, the latest runs, the event feed and the tool registry.
//
// Every figure here is arithmetic over rows the server counted — no claim mark. Where a producer
// has not reported (the runtime before its first run, a probe that does not exist yet) the cell
// says "not collected" in `fv--unknown`; where it counted zero the cell is `fv--nil`. A health leg
// that is absent from the answer is "not collected"; one present with `ok: false` is down, and
// the two never look the same.
import { useCallback, useState } from 'react';
import { getAgentEvents, getAgentsHealthFull, getAgentsSummary, getRegistry, type AgentsHealthWithHermes } from '../api/principals.js';
import { abortRun, listRuns } from '../api/runs.js';
import { Fig, Mast, PageShell, Rail, RailCell, Ref, Sheet, Tag, Zone, ZoneHead, Lnk } from '../components/PageKit.js';
import PrincipalsPanel from '../components/PrincipalsPanel.js';
import { usePoll } from '../components/usePoll.js';
import { useToast } from '../components/Toast.js';
import { useLocale } from '../i18n/index.js';
import { href, open, ROUTES, scrollToAnchor } from '../routes.js';
import { tCtx } from '../../shared/i18n.js';
import type { Run } from '../../shared/types.js';
import './AgentsPage.de.js';
import './AgentsPage.fr.js';

type DotTone = 'ok' | 'attend' | 'off';

function Dot({ tone }: { tone: DotTone }) {
  return <span className={`ag-dot${tone === 'attend' ? ' ag-dot--attend' : tone === 'off' ? ' ag-dot--off' : ''}`} />;
}

const NotCollected = ({ label }: { label: string }) => <span className="fv--unknown voice-figure">{label}</span>;

// A probe in flight is a third state, distinct from "not collected" (fv--unknown, nobody
// collected it) and from a counted value: no tone class, just the transient label.
const Checking = ({ label }: { label: string }) => <span className="fv voice-figure">{label}</span>;

export default function AgentsPage() {
  const { t, fmtDateTime } = useLocale();
  const toast = useToast();

  const fetchHealth = useCallback(() => getAgentsHealthFull(), []);
  const fetchSummary = useCallback(() => getAgentsSummary(), []);
  const fetchRuns = useCallback(() => listRuns({ limit: 20 }), []);
  const fetchEvents = useCallback(() => getAgentEvents(30), []);
  const fetchRegistry = useCallback(() => getRegistry(), []);

  const health = usePoll(fetchHealth, 60_000);
  const summary = usePoll(fetchSummary, 30_000);
  const runs = usePoll(fetchRuns, 15_000);
  const events = usePoll(fetchEvents, 15_000);
  const registry = usePoll(fetchRegistry, 300_000);

  const [createRequested, setCreateRequested] = useState(0);

  const h: AgentsHealthWithHermes | null = health.data;
  const s = summary.data;

  const overall: DotTone = !h ? 'off' : h.litellm.ok && h.db.ok ? 'ok' : 'attend';
  const checking = health.loading;

  const abort = async (run: Run) => {
    try {
      await abortRun(run.id);
      toast('ok', t('{ref} aborted.', { ref: run.ref }));
      await runs.refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    }
  };

  const sealOf = (run: Run): { cls: string; text: string } => {
    switch (run.status) {
      case 'done': return { cls: 'seal seal--ok', text: t('clean') };
      case 'failed': return { cls: 'seal seal--attend', text: tCtx(t, 'failed', 'run') };
      case 'aborted': return { cls: 'seal seal--attend', text: t('aborted') };
      case 'running': return { cls: 'seal seal--none', text: t('running') };
      default: return { cls: 'seal seal--none', text: t('queued') };
    }
  };

  const wall = (ms: number | null): string => {
    if (ms === null) return '—';
    const sTotal = Math.round(ms / 1000);
    return sTotal < 60 ? `${sTotal}s` : `${Math.floor(sTotal / 60)}m ${String(sTotal % 60).padStart(2, '0')}s`;
  };

  const notice = health.notice ?? summary.notice;

  return (
    <PageShell>
      <Mast
        kicker={t('System')}
        title={t('Agents')}
        sub={<>
          {t('one tool registry, two transports')}
          <span className="sep">·</span>
          {t('every caller is a principal with scopes')}
          <span className="sep">·</span>
          {t('the scout never touches the database')}
          {notice && <><span className="sep">·</span><span className="fv--attend">{notice}</span></>}
        </>}
        verbs={<>
          <button type="button" className="btn" onClick={() => scrollToAnchor('event-feed')}>{t('Event feed →')}</button>
          <button type="button" className="btn" onClick={() => { setCreateRequested((n) => n + 1); scrollToAnchor('principals'); }}>{t('New principal')}</button>
        </>}
      />

      <Rail
        after={
          <div className="instr-band" style={{ borderTop: 0, flexWrap: 'wrap', rowGap: 6 }}>
            <Dot tone={overall} />
            <span className="band-item"><b>{checking ? t('Checking…') : overall === 'ok' ? t('Ready') : t('Attend')}</b></span>
            <span className="sepdot">|</span>

            <span className="band-item">
              {t('LiteLLM')}{' '}
              {h ? (h.litellm.ok ? <Ref title={h.litellm.error ?? undefined}>{h.litellm.model ?? t('model unset')}</Ref> : <span className="fv--attend">{t('unreachable')}</span>) : <Checking label={t('checking…')} />}
            </span>
            <span className="sepdot">·</span>

            <span className="band-item">
              {t('Harness')}{' '}
              {!h ? <Checking label={t('checking…')} /> : !h.harness ? <NotCollected label={t('not collected')} /> : h.harness.ok
                ? <Ref title={h.harness.error ?? h.harness.bin ?? undefined}>{h.harness.version ?? h.harness.pin ?? t('found')}</Ref>
                : <span className="fv--attend" title={h.harness.error ?? undefined}>{t('not found')}</span>}
            </span>
            <span className="sepdot">·</span>

            <span className="band-item">
              {t('runtime')}{' '}
              {!h ? <Checking label={t('checking…')} /> : !h.runtime ? <NotCollected label={t('not collected')} /> : (
                <span className={h.runtime.ok ? 'voice-figure' : 'fv--attend voice-figure'} title={h.runtime.error ?? undefined}>
                  {h.runtime.active} {t('active')} · {h.runtime.queued} {t('queued')} · {t('cap {n}', { n: h.runtime.cap_now })}{h.runtime.night ? ` (${t('night')})` : ''}
                </span>
              )}
            </span>
            <span className="sepdot">·</span>

            <span className="band-item">
              {t('embedding')}{' '}
              {!h ? <Checking label={t('checking…')} /> : !h.embedding ? <NotCollected label={t('not collected')} /> : h.embedding.ok
                ? <Ref>{h.embedding.model ?? 'embedding'}</Ref>
                : <span className="fv--attend" title={h.embedding.error ?? undefined}>{t('unreachable')}</span>}
            </span>
            <span className="sepdot">·</span>

            <span className="band-item">
              {t('reranker')}{' '}
              {!h ? <Checking label={t('checking…')} /> : !h.reranker ? <NotCollected label={t('not collected')} /> : h.reranker.ok
                ? <Ref>{h.reranker.model ?? 'reranker'}</Ref>
                : <span className="fv--attend" title={h.reranker.error ?? undefined}>{t('unreachable')}</span>}
            </span>
            <span className="sepdot">·</span>

            <span className="band-item">
              {t('scout')}{' '}
              {!h ? <Checking label={t('checking…')} /> : !h.scout ? <NotCollected label={t('not collected')} /> : h.scout.ok
                ? <span className="voice-figure" title={h.scout.worker ?? undefined}>{t('seen {time}', { time: fmtDateTime(h.scout.last_claim_at) })}</span>
                : <span className="fv--attend" title={h.scout.error ?? undefined}>{h.scout.last_claim_at ? t('last seen {time}', { time: fmtDateTime(h.scout.last_claim_at) }) : tCtx(t, 'no worker', 'scout')}</span>}
            </span>
            <span className="sepdot">·</span>

            <span className="band-item">
              <Dot tone={!h ? 'off' : h.mail.mode === 'smtp' && h.mail.ok !== false ? 'ok' : 'attend'} />
              {t('mail')} <b>{h?.mail.mode ?? t('stub')}</b>
              {h?.mail.inbound && <span className="d"> · {t('inbound')} {h.mail.inbound}</span>}
            </span>
            <span className="sepdot">·</span>

            <span className="band-item">
              {t('Surfe')}{' '}
              {!h ? <Checking label={t('checking…')} /> : h.surfe.ok && h.surfe.credits
                ? <span className="voice-figure">{h.surfe.credits.search ?? '—'} {t('search')} · {h.surfe.credits.email ?? '—'} {t('email')}</span>
                : <span className="fv--attend">{t('unreachable')}</span>}
            </span>
            <span className="sepdot">·</span>

            <span className="band-item">
              {t('Hermes')}{' '}
              {!h ? <Checking label={t('checking…')} /> : !h.hermes ? <NotCollected label={t('not collected')} /> : !h.hermes.installed
                ? <span className="fv--attend" title={h.hermes.home}>{t('not installed')}</span>
                : (
                  <span title={h.hermes.home}>
                    <Ref>{h.hermes.version ?? t('installed')}</Ref>
                    {' '}<span className="d">· {h.hermes.config_has_crm ? t('config has crm') : t('config lacks crm')}</span>
                    {h.hermes.last_call_at && <span className="d"> · {t('last call')} <Fig>{fmtDateTime(h.hermes.last_call_at)}</Fig></span>}
                  </span>
                )}
            </span>
            <span className="sepdot">·</span>

            <span className="band-item">
              {t('Database')}{' '}
              {h?.db.ok ? <span className="voice-figure">{h.db.latency_ms}<span className="u">ms</span></span> : <span className="fv--attend">{h ? t('unreachable') : t('checking…')}</span>}
            </span>
            <span style={{ marginLeft: 'auto' }}>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => { void health.refresh(); void summary.refresh(); }} disabled={checking}>
                {t('Re-check')}
              </button>
            </span>
          </div>
        }
      >
        <RailCell
          label={t('Runs · 24h')}
          value={s ? s.runs_24h : t('not collected')}
          tone={!s ? 'unknown' : s.runs_24h === 0 ? 'nil' : s.runs_24h_detail.failed > 0 ? 'attend' : 'ink'}
          detail={s
            ? (s.runs_24h === 0 ? t('no run in the last 24 hours') : t('{done} done · {failed} failed · {active} active', s.runs_24h_detail))
            : summary.error ? t('the ledger did not answer') : t('counting…')}
        />
        <RailCell
          label={t('Filed')}
          go="→"
          onClick={() => open(ROUTES.review({ status: 'unheld' }))}
          value={s ? s.filed : t('not collected')}
          tone={!s ? 'unknown' : s.filed === 0 ? 'nil' : 'ink'}
          detail={s
            ? (s.filed === 0 ? t('no proposal has come from an agent yet') : t('{unheld} unheld · {held} held · {refused} refused', s.filed_detail))
            : t('proposals by agents, all time')}
        />
        <RailCell
          label={t('Pushed back')}
          go="→"
          onClick={() => open(ROUTES.review({ status: 'refused' }))}
          value={s ? s.pushed_back_7d : t('not collected')}
          tone={!s ? 'unknown' : s.pushed_back_7d === 0 ? 'nil' : 'attend'}
          detail={s
            ? (s.pushed_back_top
              ? t('{n} on {principal} · {reason}', { n: s.pushed_back_top.n, principal: s.pushed_back_top.principal, reason: t(s.pushed_back_top.refuse_reason ?? 'other') })
              : t('no agent proposal refused in 7 days'))
            : t('refused proposals by agents, 7 days')}
        />
        <RailCell
          label={t('Hermes · 24h')}
          value={s ? s.hermes_24h : t('not collected')}
          unit={s && s.hermes_24h > 0 ? t('calls') : undefined}
          tone={!s ? 'unknown' : s.hermes_24h === 0 ? 'nil' : 'ink'}
          detail={s
            ? (s.hermes_24h === 0
              ? (h?.hermes && !h.hermes.installed ? t('Hermes is not installed on this host') : t('no call from hermes in 24 hours'))
              : t('{read} read · {propose} propose · 0 commit · 0 send', {
                read: s.hermes_24h_by_scope.read ?? 0, propose: s.hermes_24h_by_scope.propose ?? 0,
              }))
            : t('ledger calls by principal hermes')}
        />
        <RailCell
          label={t('Send scope')}
          value={t('off')}
          unit={t('for every agent')}
          tone="nil"
          detail={t('only the operator sends')}
        />
      </Rail>

      <PrincipalsPanel createRequested={createRequested} />

      <Zone id="runs">
        <ZoneHead
          label={t('Runs')}
          count={runs.data ? runs.data.total : undefined}
          note={t('one run = one sentence and its seal')}
        />
        <Sheet clip>
          {!runs.data ? (
            <p className="zone-empty">
              {runs.error && !/501|not built/i.test(runs.error)
                ? t('Could not load the runs: {error}', { error: runs.error })
                : t('The runner has not reported yet. A run is one DeepSeek Harness process per skill — research, mail triage, transcript, persona — with its own principal, its own token and its own ledger rows.')}
            </p>
          ) : runs.data.items.length === 0 ? (
            <p className="zone-empty">{t('No run yet. "Research" on a person or an organisation starts one; the nightly schedule starts the rest.')}</p>
          ) : runs.data.items.map((run) => {
            const seal = sealOf(run);
            return (
              <div className="run" key={run.id}>
                <div>
                  {/* THE REF IS THE DOOR (R3f §2). A run is watched at its own desk — its steps
                      as they happen, its ledger, what it filed — and the ref is how one gets
                      there from every list that names it. */}
                  <Lnk href={href(ROUTES.run({ id: run.id }))} title={t('Open this run’s desk')}><Ref>{run.ref}</Ref></Lnk>
                  <div className="when voice-figure">{fmtDateTime(run.started_at ?? run.created_at)}</div>
                </div>
                <div>
                  <div className="pass">
                    {run.skill}
                    {run.subject_ref && <> · <Ref>{run.subject_ref}</Ref></>}
                    {run.subject_label && <> · {run.subject_label}</>}
                    {' '}<span className="d">· {t(run.triggered_by)}</span>
                  </div>
                  <div className="verdict">
                    {t('filed')} <Fig>{run.proposals_filed}</Fig>
                    {run.turns !== null && <> · <Fig>{run.turns}</Fig> {t('turns')}</>}
                    {run.tokens_in !== null && <> · <Fig>{(run.tokens_in ?? 0) + (run.tokens_out ?? 0)}</Fig> {t('tokens')}</>}
                    {run.wall_ms !== null && <> · <Fig>{wall(run.wall_ms)}</Fig></>}
                    {run.summary && <> · <span className="voice-record">{run.summary}</span></>}
                  </div>
                  {run.error && <div className="push">{run.error}</div>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                  <span className={seal.cls}>{seal.text}</span>
                  {(run.status === 'running' || run.status === 'queued') && (
                    <Lnk onClick={() => void abort(run)}>{t('Abort')}</Lnk>
                  )}
                </div>
              </div>
            );
          })}
        </Sheet>
      </Zone>

      <Zone id="event-feed">
        <ZoneHead
          label={t('Event feed')}
          count={events.data ? events.data.total : undefined}
          note={t('append-only · what the consumers and Hermes read')}
        />
          <Sheet clip>
            {!events.data ? (
              <p className="zone-empty">{events.error ? t('Could not load the events: {error}', { error: events.error }) : t('Loading…')}</p>
            ) : events.data.items.length === 0 ? (
              <p className="zone-empty">{t('No event yet. The first interaction, commitment, proposal or signal writes one.')}</p>
            ) : events.data.items.map((ev) => (
              <div className="row" key={ev.id} style={{ gridTemplateColumns: '130px 1fr auto' }}>
                <span className="voice-figure d">{fmtDateTime(ev.created_at)}</span>
                <span>
                  <Ref>{ev.kind}</Ref>
                  {ev.subject_kind && <span className="d"> · {t(ev.subject_kind)} #{ev.subject_id}</span>}
                  {typeof ev.payload?.ref === 'string' && <> · <Ref>{ev.payload.ref}</Ref></>}
                </span>
                <span className="d">{ev.principal ?? ''}</span>
              </div>
            ))}
          </Sheet>
      </Zone>

      <Zone id="tool-registry">
        <ZoneHead
          label={t('Tool registry')}
          count={registry.data ? registry.data.total : undefined}
          note={t('capabilities by scope · which principals hold them')}
        />
          <Sheet pad>
            {!registry.data ? (
              <p className="rat">{registry.error ? t('Could not load the registry: {error}', { error: registry.error }) : t('Loading…')}</p>
            ) : (
              <>
                {registry.data.groups.map((g) => (
                  <p className="rat" key={g.scope} style={{ marginBottom: 6 }}>
                    <b>{g.scope}</b> · <span className="src-ref">{g.capabilities.map((c) => c.name).join(' ')}</span>
                    {' '}
                    {g.principals.length
                      ? g.principals.map((p) => <span key={p} style={{ marginRight: 4, display: 'inline-block' }}><Tag>{p}</Tag></span>)
                      : <Tag tone="ghost">{t('no principal holds it')}</Tag>}
                  </p>
                ))}
                <p className="rat" style={{ marginBottom: 6 }}>
                  <b>send</b> · <Tag tone="ghost">{t('not a capability')}</Tag> · {t('operator only')}
                </p>
                <p className="rat" style={{ color: 'var(--ink-3)' }}>
                  {t('Defined once (P9); served as REST, as MCP over HTTP for Hermes and for every run’s harness, and over stdio for Claude Code. Scope is checked in the tool layer, never in a prompt. Every call writes one ledger row, refusals included.')}
                </p>
              </>
            )}
          </Sheet>
      </Zone>
    </PageShell>
  );
}
